import { useState } from "react";

import { ChevronRight } from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import { KeyValueGrid } from "@/pages/tina/items/components/key-value-grid";
import { PanelShell } from "@/pages/tina/items/components/panel-shell";
import type { PipelineItem } from "@/types/tina";

interface ErrorPayload {
  type?: string;
  message?: string;
  traceback?: string | null;
  retryable?: boolean;
}

export function FailedPanel({ item }: { item: PipelineItem }) {
  const err = (item.error ?? {}) as ErrorPayload;
  const [open, setOpen] = useState(false);
  return (
    <PanelShell
      status="failed"
      subtitle={`Terminal failure at ${item.current_stage ?? "—"} after attempt ${item.attempt_count} / 3.`}
    >
      <Alert variant="destructive">
        <AlertTitle className="font-mono text-xs uppercase tracking-wider">
          {err.type ?? "unknown_error"}
        </AlertTitle>
        <AlertDescription className="whitespace-pre-wrap font-mono text-[13px]">
          {err.message ?? "(no message)"}
        </AlertDescription>
      </Alert>
      <KeyValueGrid
        rows={[
          { label: "Stage", value: item.current_stage ?? "—", mono: true },
          { label: "Attempt", value: `${item.attempt_count} / 3` },
          { label: "Retryable", value: err.retryable ? "yes" : "no" },
        ]}
      />
      {err.traceback && (
        <Collapsible open={open} onOpenChange={setOpen}>
          <CollapsibleTrigger className="group inline-flex items-center gap-1 self-start text-xs font-medium text-muted-foreground hover:text-foreground">
            <ChevronRight
              className={cn(
                "h-3.5 w-3.5 transition-transform",
                open && "rotate-90",
              )}
            />
            {open ? "Hide traceback" : "Show traceback"}
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-2">
            <pre className="overflow-x-auto rounded-md border bg-muted/40 p-3 font-mono text-[11px] leading-relaxed">
              {err.traceback}
            </pre>
          </CollapsibleContent>
        </Collapsible>
      )}
    </PanelShell>
  );
}
